import { useCallback } from 'react'

import { useQuery, useQueryClient } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { convertDatesInObject } from '@/helpers/api'
import { SweetspotError } from '@/helpers/error'

import { pursuitQueryKeys } from '@/hooks/pursuits/types'
import {
  type RecentlyViewedPursuit,
  recentlyViewedPursuitsQueryKeys,
  toRecentlyViewedPursuit,
} from '@/hooks/recently-viewed/types'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'
import type { Pursuit } from '@/types/pursuit/Pursuit'

// TODO: figure out why react-query fetches 3-4 times for every "1" retry
export const RETRY_COUNT: number = 1

interface UsePursuitArgs<T> {
  pursuitId: UUID | ''
  select?: (data: Pursuit) => T
  /** Extra gate ANDed with the pursuitId check (e.g. "dialog is open"). */
  enabled?: boolean
}

export const fetchPursuit = async (
  pursuitId: UUID | '',
  accessToken: string,
): Promise<Pursuit> => {
  if (!pursuitId) {
    throw new Error('No pursuit ID provided.')
  }
  const res: Response = await fetch(`${API_URL}/api/v1/pursuits/${pursuitId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: `Failed to fetch pursuit: ${res.statusText}`,
      cause: { status: res.status },
    })
  }
  return convertDatesInObject(await res.json())
}

export const usePursuit = <T = Pursuit>({
  pursuitId,
  select,
  enabled = true,
}: UsePursuitArgs<T>) => {
  const { accessToken } = useAuthInfoWithThrow()

  const queryClient = useQueryClient()

  const selectFn = useCallback(
    (data: Pursuit) => {
      if (select) {
        return select(data)
      }
      return data as unknown as T
    },
    [select],
  )

  return useQuery({
    queryKey: pursuitQueryKeys.pursuit({ pursuitId }),
    queryFn: async () => {
      const pursuit = await fetchPursuit(pursuitId, accessToken)

      const recentlyViewedPursuitsKey =
        recentlyViewedPursuitsQueryKeys.organization({
          orgId: pursuit.organization_id,
        })
      const pursuitAsRecentlyViewed = toRecentlyViewedPursuit(pursuit)

      queryClient.setQueryData<PaginatedResponse<RecentlyViewedPursuit>>(
        recentlyViewedPursuitsKey,
        (data: PaginatedResponse<RecentlyViewedPursuit> | undefined) => {
          if (!data) {
            // don't add the pursuit to the recently viewed list if it's not found,
            // that way the query gets fetched next time the hook mounts
            return undefined
          }
          return {
            has_more: data.has_more,
            count: data.count + 1,
            data: [
              pursuitAsRecentlyViewed,
              ...(data?.data ?? []).filter(
                (pursuit) => pursuit.id !== pursuitAsRecentlyViewed.id,
              ),
            ],
          }
        },
      )

      return pursuit
    },
    select: selectFn,
    staleTime: Infinity,
    enabled: enabled && pursuitId !== '',
    retry: (failureCount: number, error: SweetspotError) => {
      const status = error.cause?.status
      if (status === 404 || status === 403) return false
      return failureCount < RETRY_COUNT
    },
  })
}

interface UsePursuitTitleArgs {
  pursuitId: UUID | ''
}

const selectPursuitTitle = (pursuit: Pursuit) => {
  return pursuit.title
}

export const usePursuitTitle = ({ pursuitId }: UsePursuitTitleArgs) => {
  return usePursuit({ pursuitId, select: selectPursuitTitle })
}
